/* eslint-disable @typescript-eslint/no-explicit-any */
import { DynamicFieldData } from '@/shared/components/DynamicForm/models/DynamicFieldData'
import { DynamicFormDefinition } from '@/shared/components/DynamicForm/models/DynamicFormSection'
import { FormFieldDefaultValue, FormFieldSelectOptions, getDynamicFormFields } from '@/shared/lib/getDynamicFormFields'

const defaultImportFields: DynamicFieldData[] = [
  {
    id: 'import-section-title',
    fieldType: 'section',
    fieldName: 'importSectionTitle',
    label: 'Import Orders',
    showDivider: false,
  },
  {
    id: 'import-target-radio-button',
    fieldType: 'radiogroup',
    fieldName: 'importTarget',
    label: 'Import Target',
    defaultValue: '',
    required: true,
    row: false,
    options: [
      { label: 'Inbox', value: 'inbox' },
      { label: 'Existing Scenario', value: 'existing' },
      { label: 'New Scenario for Date', value: 'newDate' },
      { label: 'New Scenario for Day of Week', value: 'newDay' },
    ],
    config: {
      required: 'Please select Upload Target',
    },
  },
  {
    id: 'import-order-file',
    fieldType: 'file',
    fieldName: 'uploadFile',
    label: 'Order File',
    placeholder: 'Select Order File',
    defaultValue: null,
    accept: '.xlsx,.xls',
    required: true,
    config: {
      required: 'Please select Order File to Upload',
    },
  },
  {
    fieldType: 'select',
    fieldName: 'scenario',
    label: 'Scenario',
    placeholder: 'Select Scenario',
    options: [],
    dependencies: [{
      fieldName: 'importTarget',
      values: ['existing'],
      operator: '==',
    }],
    config: {
      validate: {
        conditional: (value: any, formValues: any) => {
          if (formValues.importTarget !== 'existing') {
            return true
          }
          
          return value !== null || 'Enter/Select Date of Operation'
        },
      },
    },
  },
  {
    fieldType: 'date',
    fieldName: 'dateOfOperation',
    label: 'Date of Operation',
    defaultValue: null,
    dependencies: [{
      fieldName: 'importTarget',
      values: ['newDate'],
      operator: '==',
    }],
    disablePast: true,
    config: {
      validate: {
        conditional: (value: any, formValues: any) => {
          if (!['newDate', 'rfid'].includes(formValues.importTarget)) {
            return true
          }

          return value !== null || 'Enter/Select Date of Operation'
        },
      },
    },
  },
  {
    fieldType: 'select',
    fieldName: 'dayOfWeek',
    label: 'Day of Week',
    placeholder: 'Select Weekday',
    options: [
      { label: 'Monday', value: 'monday' },
      { label: 'Tuesday', value: 'tuesday' },
      { label: 'Wednesday', value: 'wednesday' },
      { label: 'Thursday', value: 'thursday' },
      { label: 'Friday', value: 'friday' },
      { label: 'Saturday', value: 'saturday' },
      { label: 'Sunday', value: 'sunday' },
    ],
    dependencies: [{
      fieldName: 'importTarget',
      values: ['newDay'],
      operator: '==',
    }],
    config: {
      validate: {
        conditional: (value: any, formValues: any) => {
          if (formValues.importTarget !== 'newDay') {
            return true
          }

          return value !== null || 'Please select Day of Week'
        },
      },
    },
  },
  {
    fieldType: 'text',
    fieldName: 'scenarioName',
    label: 'Scenario Name',
    placeholder: 'Enter Name for the New Scenario',
    defaultValue: '',
    dependencies: [{
      fieldName: 'importTarget',
      values: ['newDay', 'newDate'],
      condition: 'or',
      operator: '==',
    }],
    config: {
      validate: {
        conditional: (value: any, formValues: any) => {
          if (!['newDate', 'rfid'].includes(formValues.importTarget)) {
            return true
          }
          
          return value !== null || 'Enter Scenario Name'
        },
      },
    },
  },
]

type GetImportFormDefinitionArgs = {
  defaultValues?: FormFieldDefaultValue
  selectOptions?: FormFieldSelectOptions
}

export const getImportFormDefinition = ({
  defaultValues,
  selectOptions,
}: GetImportFormDefinitionArgs): DynamicFormDefinition => {
  // Update scenarios options if provided

  const importFields = getDynamicFormFields({
    fields: defaultImportFields,
    defaultValues,
    selectOptions,
  })

  return {
    formType: 'default',
    sections: [
      {
        label: 'Import Orders',
        fields: importFields,
      },
    ],
  }
}